import { makeStyles } from 'tss-react/mui';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next/';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Box from 'web-library/stories/atom/Box/Box';
import Typography from 'web-library/stories/atom/Typography/Typography';
import TextFieldForm from 'web-library/stories/atom/TextField/TextFieldForm';
import ContentTemplate from 'web-library/stories/template/ContentTemplate/ContentTemplate';
import TitleHeader from 'web-library/stories/molecule/TitleHeader/TitleHeader';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { ID_TYPE_MENU_ITEMS } from '../../modules/accountManagement/logic/useCreateCandidateForm';
import ControlledAutocomplete from '../../components/form-items/ControlledAutocomplete';
import schemaForgotUsername, { ForgotUserNameFormData } from './schemaForgotUsername';
import { IdType, RetrieveUserNameByEmailResponse } from '../../models';
import {
  usePostRetrieveUsernameByEmail
} from '../../api-refactored/user/usePostRetrieveUsernameByEmail';
import { usePostCaptchaVerifyToken } from '../../api-refactored/user/usePostCaptchaVerifyToken';
import { useGetCaptchaSiteKey } from '../../api-refactored/user/useGetCaptchaSiteKey';
import { GenericErrorResponse } from "../../api-refactored/typings";
import {typeTheme} from "web-library/theme/helper/tssHelper"
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const Captcha = lazy(() => import('web-library/stories/atom/Captcha/Captcha'));

const useStyles = makeStyles()((theme) =>
({
  formContent: {
    marginBottom: theme.spacing(7),
  },
  dropdownTextField: {
    marginBottom: theme.spacing(3),
  },
  messageContainer: {
    marginBottom: theme.spacing(2),
  },
  successMessage: {
     ...typeTheme(theme, 'helperText'),
    color: theme.color.state.stateSuccess,
  },
  errorMessage: {
    color: theme.color.state.stateError,
  },
  captcha: {
    paddingBottom: theme.spacing(3),
  },
}));

const ForgotUsername = () => {
  const { classes } = useStyles();
  const [dateOfBirth, setDateOfBirth] = useState<string>('');

  const [emailRetrievedFromUsername, setEmailRetrievedFromUsername] = useState<string>('');

  const [retrieveUserNameByEmailErrorMessage, setRetrieveUserNameByEmailErrorMessage] =
    useState<string>('');

  const [isBot, setIsBot] = useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  const { t } = useTranslation();

  const form = useForm<ForgotUserNameFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(schemaForgotUsername),
  });

  const { control, formState: {errors}, getValues, handleSubmit, register, setValue, trigger, watch } = form;

  const onSuccessRetrieveUserNameByEmailStatus = (res: RetrieveUserNameByEmailResponse) => {
    setEmailRetrievedFromUsername(res.email);
    setRetrieveUserNameByEmailErrorMessage('');
    setShowCaptcha(false);
  };

  const onErrorRetrieveUserNameByEmailStatus = (err: AxiosError<GenericErrorResponse>) => {
    setRetrieveUserNameByEmailErrorMessage(err?.response?.data.message!);
    setEmailRetrievedFromUsername('');
    setShowCaptcha(false);
    if (err.response?.data?.captchaData?.isLocked) {
      setShowCaptcha(true);
    }
  };

  const { mutate: mutatePostRetrieveUserNameByEmail, status: retrieveUserNameByEmailStatus } =
    usePostRetrieveUsernameByEmail({
      onSuccess: onSuccessRetrieveUserNameByEmailStatus,
      onError: onErrorRetrieveUserNameByEmailStatus,
    });

  const retrieveUsernameByEmail = () => {
    mutatePostRetrieveUserNameByEmail({
      userIdType: getValues('IdType') === 'I' ? IdType.NRIC_OR_FIN : IdType.PASSPORT,
      nricFinPassport: getValues('NricFinPassportNo'),
      dateOfBirth: getValues('dateOfBirth'),
    });
  };

  useEffect(() => {
    const idTypeValue = watch('IdType');
    const nricFinPassportNoValue = watch('NricFinPassportNo');

    if ((idTypeValue === 'I' || idTypeValue === 'P') && nricFinPassportNoValue.trim().length) {
      trigger('NricFinPassportNo').then(() => null);
    }
  }, [trigger, watch]);

  const checkIsBot = (result: boolean) => setIsBot(result);
  const captchaVerifyTokenMutation = usePostCaptchaVerifyToken({
    onSuccess: (data) => setIsBot(data ? data.isBot : true),
    onError: () => setIsBot(true),
  });

  return (
    <>
      <TitleHeader title={t('profile->>ForgottenUserNameForm.ForgotUsername')} />
      <ContentTemplate>
        <form onSubmit={handleSubmit(retrieveUsernameByEmail)}>
          <Box className={classes.formContent}>
            <ControlledAutocomplete<{key: string, value: string, children: string}, false, false, undefined>
              label={t('profile->>ForgottenUserNameForm.IdType')}
              className={classes.dropdownTextField}
              isRequired
              control={control}
              name="IdType"
              id="inputSelect-idType"
              error={!!errors.IdType}
              helperText={errors?.IdType?.message?.toString()}
              options={ID_TYPE_MENU_ITEMS}
              getOptionLabel={({ children }) => children}
              placeholder={t('profile->>ForgottenUserNameForm.IdTypePlaceHolder')}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? null,
                output: (selectedOption) => (selectedOption ? selectedOption.value : null),
              }}
            />
            <TextFieldForm
              form={form}
              label={t('profile->>ForgottenUserNameForm.NricFinPassportNo')}
              className={classes.dropdownTextField}
              isRequired
              name="NricFinPassportNo"
              type="text"
              id="inputText-candidate-nric-fin-pass-no"
              placeholder={t('profile->>ForgottenUserNameForm.NricFinPassportNoPlaceholder')}
              size="medium"
              error={!!errors.NricFinPassportNo}
              helperText={errors?.NricFinPassportNo?.message?.toString()}
            />
            <ControlledDatePicker
              control={control}
              label={t('profile->>ForgottenUserNameForm.DateOfBirth')}
              className={classes.dropdownTextField}
              isRequired
              onChange={(date) => {
                setValue('dateOfBirth', date || '');
              }}
              inputVariant="filled"
              name="dateOfBirth"
              error={!!errors.dateOfBirth}
              helperText={errors?.dateOfBirth?.message?.toString()}
              disableFuture
            />

            {showCaptcha && (
              <Suspense fallback={null}>
                <Captcha
                  {...{
                    id: 'forgot-password-captcha',
                    className: classes.captcha,
                    checkIsBot,
                    captchaSiteKeyMutation: useGetCaptchaSiteKey,
                    captchaVerifyTokenMutation,
                  }}
                />
              </Suspense>
            )}

            {emailRetrievedFromUsername && (
              <Box className={classes.messageContainer}>
                <Typography className={classes.successMessage} display="inline" variant="body2">
                  {t('profile->>ForgottenUserNameForm.RetrieveNameByEmailSuccessfulMessage', {
                    partiallyMaskedEmail: emailRetrievedFromUsername,
                  })}
                </Typography>
              </Box>
            )}
            {retrieveUserNameByEmailErrorMessage && (
              <Box className={classes.messageContainer}>
                <Typography className={classes.errorMessage} display="inline" variant="body2">
                  {retrieveUserNameByEmailErrorMessage}
                </Typography>
              </Box>
            )}
            <ApiButton
              id="button-forgotUsername-confirmDetails"
              status={retrieveUserNameByEmailStatus}
              type="submit"
              disabled={isBot}
            >
              {t('profile->>ForgottenUserNameForm.ConfirmDetails')}
            </ApiButton>
          </Box>
        </form>
      </ContentTemplate>
    </>
  );
};

export default ForgotUsername;
